// Payment Return Handler Component
// File: src/jsx/components/Common/PaymentReturnHandler.jsx

import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  verifyICICIResponseHash,
  debugICICIResponseHash,
  updatePaymentStatusToBackend,
} from "../../../utils/paymentUtils";

const PaymentReturnHandler = () => {
  console.log("🎯 PaymentReturnHandler component rendered");

  const location = useLocation();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [paymentResult, setPaymentResult] = useState(null);
  const toastShownRef = useRef(false);
  const processedPaymentRef = useRef(null);
  const toastIdRef = useRef(null);
  const isInitializedRef = useRef(false);

  console.log("📍 Location object:", location);
  console.log("🔗 Navigate function:", typeof navigate);

  useEffect(() => {
    console.log("🔍 PaymentReturnHandler useEffect triggered");
    console.log("📍 Current location:", location.pathname);
    console.log("🔗 Search params:", location.search);
    console.log("📄 Full URL:", window.location.href);

    // Prevent multiple initializations in Strict Mode
    if (isInitializedRef.current) {
      console.log("🔄 Already initialized, skipping...");
      return;
    }

    isInitializedRef.current = true;

    // Clear any existing toasts on component mount
    toast.dismiss();

    // Process payment return
    handlePaymentReturn();

    // Cleanup function to reset flags when component unmounts
    return () => {
      toastShownRef.current = false;
      processedPaymentRef.current = null;
      isInitializedRef.current = false;
      // Dismiss specific toast if exists
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    };
  }, []); // Only run once when component mounts

  const handlePaymentReturn = async () => {
    try {
      console.log("🔄 Starting handlePaymentReturn");

      // Create a unique identifier for this payment return
      const paymentId = location.search;

      // Check if we've already processed this exact payment return
      if (processedPaymentRef.current === paymentId) {
        console.log("🔄 Payment already processed, skipping...");
        return;
      }

      // Mark this payment as being processed
      processedPaymentRef.current = paymentId;
      toastShownRef.current = false;

      // Additional check to prevent multiple executions
      if (!isInitializedRef.current) {
        console.log("🔄 Component not initialized, skipping...");
        return;
      }

      setIsProcessing(true);

      // Get URL parameters from ICICI response
      const urlParams = new URLSearchParams(location.search);

      // ICICI specific parameters - handle multiple possible parameter names
      const merchantTxnNo =
        urlParams.get("merchantTxnNo") || urlParams.get("tranCtx");
      const responseCode =
        urlParams.get("responseCode") ||
        urlParams.get("response_code") ||
        urlParams.get("code");
      const responseDescription =
        urlParams.get("respDescription") || // Fixed: ICICI sends respDescription
        urlParams.get("responseDescription") ||
        urlParams.get("response_description") ||
        urlParams.get("description");
      const secureHash = urlParams.get("secureHash") || urlParams.get("hash");
      const amount = urlParams.get("amount");

      // Handle paymentDateTime format (YYYYMMDDHHMMSS) - split into date and time
      const paymentDateTime = urlParams.get("paymentDateTime");
      let txnDate = urlParams.get("txnDate") || urlParams.get("txn_date");
      let txnTime = urlParams.get("txnTime");

      // If paymentDateTime is provided, extract date and time from it
      if (paymentDateTime && paymentDateTime.length >= 14) {
        txnDate = paymentDateTime.substring(0, 8); // YYYYMMDD
        txnTime = paymentDateTime.substring(8, 14); // HHMMSS
      }

      const aggregatorID =
        urlParams.get("aggregatorID") || urlParams.get("aggregator_id");
      const bankTxnNo = urlParams.get("txnID"); // Fixed: ICICI sends txnID
      const cardMask = urlParams.get("cardMask");
      const paymentMode = urlParams.get("paymentMode");
      const addlParam1 = urlParams.get("addlParam1");
      const addlParam2 = urlParams.get("addlParam2");
      const customerEmailID = urlParams.get("customerEmailID");
      const customerMobileNo = urlParams.get("customerMobileNo");
      const merchantId = urlParams.get("merchantId");
      const txnID = urlParams.get("txnID");
      const status = urlParams.get("status");
      const error = urlParams.get("error");

      console.log("🔍 ICICI Payment Return Parameters:", {
        merchantTxnNo,
        responseCode,
        responseDescription,
        secureHash,
        amount,
        txnDate,
        txnTime,
        aggregatorID,
        bankTxnNo,
        cardMask,
        paymentMode,
        addlParam1,
        addlParam2,
        customerEmailID,
        customerMobileNo,
        merchantId,
        txnID,
        paymentDateTime,
        status,
        error,
        fullUrl: location.search,
      });

      // // Verify secure hash if provided (optional - for additional security)
      // if (secureHash) {
      //   const responseParams = {
      //     merchantTxnNo,
      //     responseCode,
      //     responseDescription,
      //     amount,
      //     txnDate,
      //     txnTime,
      //     aggregatorID,
      //     bankTxnNo,
      //     cardMask,
      //     paymentMode,
      //     addlParam1,
      //     addlParam2,
      //     customerEmailID,
      //     customerMobileNo,
      //     merchantId,
      //     txnID,
      //     paymentDateTime,
      //   };

      //   const isHashValid = verifyICICIResponseHash(responseParams, secureHash);

      //   if (!isHashValid) {
      //     console.warn(
      //       "⚠️ Hash verification failed - but continuing with success page check"
      //     );
      //     // Debug all possible hash patterns (for development only)
      //     if (import.meta.env.MODE === "development") {
      //       debugICICIResponseHash(responseParams, secureHash);
      //     }
      //   } else {
      //     console.log("✅ Hash verification successful");
      //   }
      // }

      // Check if this is a cancel request
      if (location.pathname.includes("/cancel")) {
        console.log("❌ Payment cancelled by user");

        // Get stored payment data for backend update
        const storedPayment = localStorage.getItem("currentPayment");
        let paymentData = {};

        if (storedPayment) {
          try {
            paymentData = JSON.parse(storedPayment);
          } catch (e) {
            console.warn("Failed to parse stored payment data:", e);
          }
        }

        // Forward ALL the same parameters that were sent to initiateSale
        const backendPaymentData = {
          token: localStorage.getItem("token"),
          admin_id: localStorage.getItem("admin_id"),
          patient_id: paymentData.patient_id || paymentData.patientId || "",
          payment_status: "denied",
          payment_response: JSON.stringify({
            merchantTxnNo: merchantTxnNo || "",
            responseCode: responseCode || "",
            responseDescription: responseDescription || "",
            secureHash: secureHash || "",
            txnDate: txnDate || "",
            txnTime: txnTime || "",
            aggregatorID: aggregatorID || "",
            bankTxnNo: bankTxnNo || "",
            cardMask: cardMask || "",
            paymentMode: paymentMode || "",
            addlParam1: addlParam1 || "",
            addlParam2: addlParam2 || "",
            customerEmailID: customerEmailID || "",
            customerMobileNo: customerMobileNo || "",
            merchantId: merchantId || "",
            txnID: txnID || "",
            status: status || "",
            error: error || "",
            paymentDateTime: paymentDateTime || "",
            fullUrl: location.search || "",
          }),
          transaction_id: merchantTxnNo || "Unknown",
          // Forward ALL the same parameters from initiateSale
          appointment_id: paymentData.appointment_id || paymentData.appointmentId || "",
          lead_id: paymentData.lead_id || paymentData.leadId || "",
          prescription_id: paymentData.prescription_id || paymentData.prescriptionId || "",
          queue_id: paymentData.queue_id || paymentData.queueId || "",
          payment_for: paymentData.payment_for || paymentData.paymentFor || "APPOINTMENT",
          amount: amount || paymentData.amount || 0,
          customer_name: paymentData.customer_name || paymentData.customerName || "",
          customer_email: paymentData.customer_email || paymentData.customerEmail || "",
          customer_mobile: paymentData.customer_mobile || paymentData.customerPhone || "",
          customer_address: paymentData.customer_address || paymentData.customerAddress || "",
          description: paymentData.description || "",
          payment_type: paymentData.payment_type || paymentData.paymentType || "UPI",
          coupon_code: paymentData.coupon_code || paymentData.couponCode || "",
          final_amount: paymentData.final_amount || paymentData.finalAmount || amount || 0,
          reference_id: paymentData.reference_id || paymentData.referenceId || paymentData.appointment_id || paymentData.lead_id || paymentData.prescription_id || "",
        };

        // 🔍 DEBUG: Log all parameters being sent to updatePaymentStatus API
        console.log("🔍 DEBUG: PaymentReturnHandler - updatePaymentStatus API Parameters");
        console.log("📍 Source: PaymentReturnHandler.handlePaymentReturn (CANCELLATION)");
        console.log("📋 Raw backendPaymentData:", backendPaymentData);
        console.log("🔑 Token:", backendPaymentData.token ? "Present" : "Missing");
        console.log("👤 Admin ID:", backendPaymentData.admin_id);
        console.log("🏥 Patient ID:", backendPaymentData.patient_id);
        console.log("📅 Appointment ID:", backendPaymentData.appointment_id);
        console.log("🎯 Lead ID:", backendPaymentData.lead_id);
        console.log("💊 Prescription ID:", backendPaymentData.prescription_id);
        console.log("📋 Queue ID:", backendPaymentData.queue_id);
        console.log("💰 Amount:", backendPaymentData.amount);
        console.log("💳 Payment Type:", backendPaymentData.payment_type);
        console.log("🎯 Payment For:", backendPaymentData.payment_for);
        console.log("🎫 Transaction ID:", backendPaymentData.transaction_id);
        console.log("🏷️ Reference ID:", backendPaymentData.reference_id);
        console.log("🎫 Coupon Code:", backendPaymentData.coupon_code);
        console.log("💰 Final Amount:", backendPaymentData.final_amount);
        console.log("📊 Payment Status:", backendPaymentData.payment_status);

        // Update payment status to backend
        try {
          console.log(
            "📤 Updating payment status to backend for cancellation..."
          );
          const updateResult = await updatePaymentStatusToBackend(
            backendPaymentData
          );

          if (updateResult.success) {
            console.log(
              "✅ Payment cancellation status updated successfully in backend"
            );
          } else {
            console.warn(
              "⚠️ Failed to update payment cancellation status in backend:",
              updateResult.error
            );
          }
        } catch (error) {
          console.error(
            "❌ Error updating payment cancellation status to backend:",
            error
          );
        }

        setPaymentResult({
          status: "CANCELLED",
          message: "Payment was cancelled by the user",
          transactionNumber: merchantTxnNo || "Unknown",
        });
        toast.info("Payment was cancelled");
        setIsProcessing(false);
        return;
      }

      // Handle different response scenarios
      // Check for ICICI success page URL pattern
      const isICICISuccessPage =
        window.location.href.includes("qa.phicommerce.com/pg/api/pgresponse") ||
        window.location.href.includes("phicommerce.com/pg/api/pgresponse");

      // Check for success indicators
      const isSuccess =
        status === "success" ||
        responseCode === "000" ||
        responseCode === "R1000" ||
        responseCode === "0000"; /*||
        isICICISuccessPage ||
        document.body.textContent.includes("payment is successfull") ||
        document.body.textContent.includes("payment is successful"); */

      if (isSuccess) {
        console.log("✅ Payment successful");

        // Get stored payment data for backend update
        const storedPayment = localStorage.getItem("currentPayment");
        let paymentData = {};

        if (storedPayment) {
          try {
            paymentData = JSON.parse(storedPayment);
          } catch (e) {
            console.warn("Failed to parse stored payment data:", e);
          }
        }

        // Forward ALL the same parameters that were sent to initiateSale
        const backendPaymentData = {
          token: localStorage.getItem("token"),
          admin_id: localStorage.getItem("admin_id"),
          patient_id: paymentData.patient_id || paymentData.patientId || "",
          payment_status: "success",
          payment_response: JSON.stringify({
            merchantTxnNo: merchantTxnNo || "",
            responseCode: responseCode || "",
            responseDescription: responseDescription || "",
            secureHash: secureHash || "",
            txnDate: txnDate || "",
            txnTime: txnTime || "",
            aggregatorID: aggregatorID || "",
            bankTxnNo: bankTxnNo || "",
            cardMask: cardMask || "",
            paymentMode: paymentMode || "",
            addlParam1: addlParam1 || "",
            addlParam2: addlParam2 || "",
            customerEmailID: customerEmailID || "",
            customerMobileNo: customerMobileNo || "",
            merchantId: merchantId || "",
            txnID: txnID || "",
            status: status || "",
            error: error || "",
            paymentDateTime: paymentDateTime || "",
            fullUrl: location.search || "",
          }),
          transaction_id: merchantTxnNo || "Unknown",
          // Forward ALL the same parameters from initiateSale
          appointment_id: paymentData.appointment_id || paymentData.appointmentId || "",
          lead_id: paymentData.lead_id || paymentData.leadId || "",
          prescription_id: paymentData.prescription_id || paymentData.prescriptionId || "",
          queue_id: paymentData.queue_id || paymentData.queueId || "",
          payment_for: paymentData.payment_for || paymentData.paymentFor || "APPOINTMENT",
          amount: amount || paymentData.amount || 0,
          customer_name: paymentData.customer_name || paymentData.customerName || "",
          customer_email: paymentData.customer_email || paymentData.customerEmail || "",
          customer_mobile: paymentData.customer_mobile || paymentData.customerPhone || "",
          customer_address: paymentData.customer_address || paymentData.customerAddress || "",
          description: paymentData.description || "",
          payment_type: paymentData.payment_type || paymentData.paymentType || "UPI",
          coupon_code: paymentData.coupon_code || paymentData.couponCode || "",
          final_amount: paymentData.final_amount || paymentData.finalAmount || amount || 0,
          reference_id: paymentData.reference_id || paymentData.referenceId || paymentData.appointment_id || paymentData.lead_id || paymentData.prescription_id || "",
        };

        // 🔍 DEBUG: Log all parameters being sent to updatePaymentStatus API
        console.log("🔍 DEBUG: PaymentReturnHandler - updatePaymentStatus API Parameters");
        console.log("📍 Source: PaymentReturnHandler.handlePaymentReturn (SUCCESS)");
        console.log("📋 Raw backendPaymentData:", backendPaymentData);
        console.log("🔑 Token:", backendPaymentData.token ? "Present" : "Missing");
        console.log("👤 Admin ID:", backendPaymentData.admin_id);
        console.log("🏥 Patient ID:", backendPaymentData.patient_id);
        console.log("📅 Appointment ID:", backendPaymentData.appointment_id);
        console.log("🎯 Lead ID:", backendPaymentData.lead_id);
        console.log("💊 Prescription ID:", backendPaymentData.prescription_id);
        console.log("📋 Queue ID:", backendPaymentData.queue_id);
        console.log("💰 Amount:", backendPaymentData.amount);
        console.log("💳 Payment Type:", backendPaymentData.payment_type);
        console.log("🎯 Payment For:", backendPaymentData.payment_for);
        console.log("🎫 Transaction ID:", backendPaymentData.transaction_id);
        console.log("🏷️ Reference ID:", backendPaymentData.reference_id);
        console.log("🎫 Coupon Code:", backendPaymentData.coupon_code);
        console.log("💰 Final Amount:", backendPaymentData.final_amount);
        console.log("📊 Payment Status:", backendPaymentData.payment_status);
        
        // Log stored payment data sources
        console.log("🔍 DEBUG: Stored Payment Data Sources");
        console.log("📋 Stored paymentData:", paymentData);
        console.log("💰 URL Amount:", amount);
        console.log("🎫 URL Transaction Number:", merchantTxnNo);

        // Update payment status to backend
        try {
          console.log("📤 Updating payment status to backend for success...");
          const updateResult = await updatePaymentStatusToBackend(
            backendPaymentData
          );

          if (updateResult.success) {
            console.log("✅ Payment status updated successfully in backend");
          } else {
            console.log(
              "🔍 backendPaymentData:",
              JSON.stringify(backendPaymentData)
            );
            console.warn(
              "⚠️ Failed to update payment status in backend:",
              updateResult.error
            );
          }
        } catch (error) {
          console.error("❌ Error updating payment status to backend:", error);
        }

        // Payment was successful
        setPaymentResult({
          status: "SUCCESS",
          message: "Payment completed successfully!",
          transactionNumber: merchantTxnNo || "Unknown",
          amount: amount,
          transactionDate: txnDate || new Date().toISOString(),
        });

        // Show toast only once and prevent duplicates
        if (!toastShownRef.current) {
          // Dismiss previous toast if exists
          if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
          }
          // Show new toast and store its ID
          toastIdRef.current = toast.success("Payment completed successfully!");
          toastShownRef.current = true;
        }

        // Clear stored payment data
        localStorage.removeItem("currentPayment");

        // Always redirect to success page for successful payments
        console.log("🔄 Redirecting to payment success page");

        // Build success URL with ALL initiateSale parameters
        const successParams = new URLSearchParams();
        
        // Payment gateway response parameters
        successParams.set("acqID", urlParams.get("acqID") || "ACQ032");
        successParams.set("transactionNumber", merchantTxnNo || "");
        successParams.set("amount", amount || "");
        successParams.set("responseCode", responseCode || "");
        successParams.set("txnID", txnID || "");
        successParams.set("paymentDateTime", paymentDateTime || "");
        successParams.set("customerEmailID", customerEmailID || "");
        successParams.set("customerMobileNo", customerMobileNo || "");
        successParams.set("merchantId", merchantId || "");
        successParams.set("paymentMode", paymentMode || "");
        
        // ✅ ALL initiateSale parameters
        successParams.set("patient_id", paymentData.patient_id || paymentData.patientId || "");
        successParams.set("appointment_id", paymentData.appointment_id || paymentData.appointmentId || "");
        successParams.set("lead_id", paymentData.lead_id || paymentData.leadId || "");
        successParams.set("prescription_id", paymentData.prescription_id || paymentData.prescriptionId || "");
        successParams.set("queue_id", paymentData.queue_id || paymentData.queueId || "");
        successParams.set("payment_for", paymentData.payment_for || paymentData.paymentFor || "");
        successParams.set("payment_type", paymentData.payment_type || paymentData.paymentType || "");
        successParams.set("customer_name", paymentData.customer_name || paymentData.customerName || "");
        successParams.set("customer_email", paymentData.customer_email || paymentData.customerEmail || "");
        successParams.set("customer_mobile", paymentData.customer_mobile || paymentData.customerPhone || "");
        successParams.set("customer_address", paymentData.customer_address || paymentData.customerAddress || "");
        successParams.set("description", paymentData.description || "");
        successParams.set("reference_id", paymentData.reference_id || paymentData.referenceId || paymentData.appointment_id || paymentData.lead_id || paymentData.prescription_id || "");

        const successUrl = `/payment/success?${successParams.toString()}`;
        console.log("🎯 Redirecting to success with ALL initiateSale parameters:", successUrl);

        // Redirect to success page
        window.location.href = successUrl;
        return;
      }

      if (
        status === "failed" ||
        error ||
        (responseCode && responseCode !== "000" && responseCode !== "0000" && responseCode !== "R1000")
      ) {
        console.log("❌ Payment failed");

        // Get stored payment data for backend update
        const storedPayment = localStorage.getItem("currentPayment");
        let paymentData = {};

        if (storedPayment) {
          try {
            paymentData = JSON.parse(storedPayment);
          } catch (e) {
            console.warn("Failed to parse stored payment data:", e);
          }
        }

        // Forward ALL the same parameters that were sent to initiateSale
        const backendPaymentData = {
          token: localStorage.getItem("token"),
          admin_id: localStorage.getItem("admin_id"),
          patient_id: paymentData.patient_id || paymentData.patientId || "",
          payment_status: "failed",
          payment_response: JSON.stringify({
            merchantTxnNo: merchantTxnNo || "",
            responseCode: responseCode || "",
            responseDescription: responseDescription || "",
            secureHash: secureHash || "",
            txnDate: txnDate || "",
            txnTime: txnTime || "",
            aggregatorID: aggregatorID || "",
            bankTxnNo: bankTxnNo || "",
            cardMask: cardMask || "",
            paymentMode: paymentMode || "",
            addlParam1: addlParam1 || "",
            addlParam2: addlParam2 || "",
            customerEmailID: customerEmailID || "",
            customerMobileNo: customerMobileNo || "",
            merchantId: merchantId || "",
            txnID: txnID || "",
            status: status || "",
            error: error || "",
            paymentDateTime: paymentDateTime || "",
            fullUrl: location.search || "",
          }),
          transaction_id: merchantTxnNo || "Unknown",
          // Forward ALL the same parameters from initiateSale
          appointment_id: paymentData.appointment_id || paymentData.appointmentId || "",
          lead_id: paymentData.lead_id || paymentData.leadId || "",
          prescription_id: paymentData.prescription_id || paymentData.prescriptionId || "",
          queue_id: paymentData.queue_id || paymentData.queueId || "",
          payment_for: paymentData.payment_for || paymentData.paymentFor || "APPOINTMENT",
          amount: amount || paymentData.amount || 0,
          customer_name: paymentData.customer_name || paymentData.customerName || "",
          customer_email: paymentData.customer_email || paymentData.customerEmail || "",
          customer_mobile: paymentData.customer_mobile || paymentData.customerPhone || "",
          customer_address: paymentData.customer_address || paymentData.customerAddress || "",
          description: paymentData.description || "",
          payment_type: paymentData.payment_type || paymentData.paymentType || "UPI",
          coupon_code: paymentData.coupon_code || paymentData.couponCode || "",
          final_amount: paymentData.final_amount || paymentData.finalAmount || amount || 0,
          reference_id: paymentData.reference_id || paymentData.referenceId || paymentData.appointment_id || paymentData.lead_id || paymentData.prescription_id || "",
        };

        // Update payment status to backend
        try {
          console.log("📤 Updating payment status to backend for failure...");
          // const updateResult = await updatePaymentStatusToBackend(
          //   backendPaymentData
          // );

          // if (updateResult.success) {
          //   console.log(
          //     "✅ Payment failure status updated successfully in backend"
          //   );
          // } else {
          //   console.warn(
          //     "⚠️ Failed to update payment failure status in backend:",
          //     updateResult.error
          //   );
          // }
        } catch (error) {
          console.error(
            "❌ Error updating payment failure status to backend:",
            error
          );
        }

        // Payment failed
        setPaymentResult({
          status: "FAILED",
          message: "Payment failed. Please try again.",
          transactionNumber: merchantTxnNo || "Unknown",
          error: responseDescription || error || "Unknown error",
        });

        // Show toast only once and prevent duplicates
        if (!toastShownRef.current) {
          // Dismiss previous toast if exists
          if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
          }
          // Show new toast and store its ID
          toastIdRef.current = toast.error("Payment failed. Please try again.");
          toastShownRef.current = true;
        }

        setIsProcessing(false);
        return;
      }

      // If no specific status, show pending
      console.log("⏳ Payment status unclear, showing pending");
      setPaymentResult({
        status: "PENDING",
        message: "Payment status is being verified...",
        transactionNumber: merchantTxnNo || "Unknown",
        note: "Please check with your bank or try again later.",
      });

      // Show toast only once and prevent duplicates
      if (!toastShownRef.current) {
        // Dismiss previous toast if exists
        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current);
        }
        // Show new toast and store its ID
        toastIdRef.current = toast.info("Payment status is being verified...");
        toastShownRef.current = true;
      }

      setIsProcessing(false);
    } catch (err) {
      console.error("❌ Error in handlePaymentReturn:", err);
      setPaymentResult({
        status: "ERROR",
        message: "Error processing payment response",
        error: err.message,
      });
      setIsProcessing(false);
    }
  };

  const handleBackToApp = () => {
    console.log("🏠 Navigating back to app");
    navigate("/");
  };

  const handleRetryPayment = () => {
    console.log("🔄 Retrying payment");
    navigate("/");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "SUCCESS":
        return "✅";
      case "FAILED":
        return "❌";
      case "CANCELLED":
        return "⚠️";
      case "PENDING":
        return "⏳";
      default:
        return "❓";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "SUCCESS":
        return "text-success";
      case "FAILED":
        return "text-danger";
      case "CANCELLED":
        return "text-warning";
      case "PENDING":
        return "text-info";
      default:
        return "text-secondary";
    }
  };

  console.log("🎨 Rendering PaymentReturnHandler UI");
  console.log("📊 Current state:", { isProcessing, paymentResult });

  if (isProcessing) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="mt-3">Processing Payment Response...</h5>
                <p className="text-muted">
                  Please wait while we verify your payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body text-center">
              {paymentResult && (
                <>
                  <div className="mb-4">
                    <span className="display-4">
                      {getStatusIcon(paymentResult.status)}
                    </span>
                  </div>

                  <h4
                    className={`mb-3 ${getStatusColor(paymentResult.status)}`}
                  >
                    {paymentResult.message}
                  </h4>

                  {paymentResult.transactionNumber && (
                    <div className="mb-3">
                      <strong>Transaction Number:</strong>
                      <br />
                      <code className="bg-light px-2 py-1 rounded">
                        {paymentResult.transactionNumber}
                      </code>
                    </div>
                  )}

                  {paymentResult.amount && (
                    <div className="mb-3">
                      <strong>Amount:</strong> ₹{paymentResult.amount}
                    </div>
                  )}

                  {paymentResult.transactionDate && (
                    <div className="mb-3">
                      <strong>Date:</strong> {paymentResult.transactionDate}
                    </div>
                  )}

                  {paymentResult.note && (
                    <div className="mb-3">
                      <div className="alert alert-info">
                        <strong>Note:</strong> {paymentResult.note}
                      </div>
                    </div>
                  )}

                  {paymentResult.error && (
                    <div className="mb-3">
                      <div className="alert alert-danger">
                        <strong>Error:</strong> {paymentResult.error}
                      </div>
                    </div>
                  )}

                  <div className="d-grid gap-2">
                    {/* <button
                      className="btn btn-primary"
                      onClick={handleBackToApp}
                    >
                      Back to Application
                    </button> */}

                    {/* {paymentResult.status === "FAILED" && (
                      <button
                        className="btn btn-outline-primary"
                        onClick={handleRetryPayment}
                      >
                        Retry Payment
                      </button>
                    )} */}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReturnHandler;
